<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { ExportImageOptions } from '../types/sketcher'
import BaseDialog from '../ui/components/BaseDialog.vue'
import BaseField from '../ui/components/BaseField.vue'
import { useDialog } from '../ui/dialogs'

type Format = 'png' | 'jpg' | 'svg'

const { close } = useDialog<ExportImageOptions>()

const format      = ref<Format>('png')
const longEdge    = ref(1200)
const bgColor     = ref('#FFFFFF')
const transparent = ref(false)
const padding     = ref(5)

// JPG has no alpha. Flip transparent off when JPG is chosen so the UI reflects
// what the engine will actually render — otherwise we'd ship a token the engine
// silently substitutes to white.
watch(format, (f) => {
  if (f === 'jpg' && transparent.value) transparent.value = false
})

const transparentAllowed = computed(() => format.value !== 'jpg')

function submit() {
  const background = transparent.value && transparentAllowed.value ? 'transparent' : bgColor.value
  close({
    format:         format.value,
    background,
    paddingPercent: padding.value,
    maxEdgePx:      longEdge.value,
  } as ExportImageOptions)
}
</script>

<template>
  <BaseDialog title="Export" @dismiss="close()">
    <form id="export-form" @submit.prevent="submit">
      <BaseField label="Format">
        <template #default="{ id }">
          <select :id="id" v-model="format" class="input">
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
            <option value="svg">SVG</option>
          </select>
        </template>
      </BaseField>

      <BaseField
        label="Long edge"
        :hint="format === 'svg' ? 'SVG user units (CSS pixels when embedded unsized).' : 'Pixels. The output’s longer edge is at most this many; the other scales to preserve aspect ratio.'"
      >
        <template #default="{ id }">
          <input :id="id" v-model.number="longEdge" type="number" min="100" max="8000" step="1" required class="input" />
        </template>
      </BaseField>

      <BaseField label="Background">
        <template #default="{ id }">
          <div class="bg-row">
            <input
              :id="id"
              v-model="bgColor"
              type="color"
              class="color"
              :disabled="transparent && transparentAllowed"
            />
            <label class="check-row">
              <input v-model="transparent" type="checkbox" class="checkbox" :disabled="!transparentAllowed" />
              <span>Transparent<span v-if="!transparentAllowed" class="muted"> (JPG has no alpha)</span></span>
            </label>
          </div>
        </template>
      </BaseField>

      <BaseField label="Padding" hint="Percent of the longer edge added around the drawing.">
        <template #default="{ id }">
          <input :id="id" v-model.number="padding" type="number" min="0" max="100" step="1" required class="input" />
        </template>
      </BaseField>
    </form>

    <template #actions>
      <button type="button" class="btn btn-secondary" @click="close()">Cancel</button>
      <button type="submit" form="export-form" class="btn btn-primary">Export</button>
    </template>
  </BaseDialog>
</template>

<style scoped>
form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.bg-row {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.color {
  width: 3rem;
  height: 2rem;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: none;
  cursor: pointer;
}

.color:disabled {
  opacity: 0.5;
  cursor: default;
}

.check-row {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: 0.85rem;
  cursor: pointer;
}

.muted {
  color: var(--fg-muted);
}
</style>
